import { useState } from 'react'

import { LotLineageTimeline } from './lot-lineage-timeline'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useOrderInventoryDeducted } from '@/hooks/use-sales-order-query'
import { isVirtualLot } from '@/lib/api/sales-order-api'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    open: boolean
    orderId: string
    /**
     * Trạng thái đơn. Đơn đã hủy thì khoản nợ lô cũng đã đóng — kho sẽ KHÔNG
     * nhập bù nữa, nên lời nhắc phải đổi, không hứa hão với người đang tra cứu.
     */
    orderStatus?: string
    onOpenChange: (open: boolean) => void
}

export function OrderLotTraceDialog({
    open,
    orderId,
    orderStatus,
    onOpenChange,
}: Props) {
    const debtClosed = orderStatus === 'cancelled'
    const { data: lots = [], isLoading } = useOrderInventoryDeducted(
        orderId,
        open,
    )
    const [active, setActive] = useState<string | undefined>(undefined)
    const firstLotId = lots[0]?.inventory_lot_id
    const value = active ?? firstLotId

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>
                        {m.order_lot_trace_dialog_title()}
                    </DialogTitle>
                </DialogHeader>
                {!isLoading && lots.length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-10">
                        {m.order_lot_trace_empty()}
                    </div>
                )}
                {lots.length > 0 && value && (
                    <Tabs
                        value={value}
                        onValueChange={setActive}
                        className="flex-1 flex flex-col overflow-hidden"
                    >
                        <div className="w-full overflow-x-auto">
                            <TabsList className="w-max">
                                {lots.map((l) => (
                                    <TabsTrigger
                                        key={l.inventory_lot_id}
                                        value={l.inventory_lot_id}
                                    >
                                        {isVirtualLot(l)
                                            ? m.order_lot_trace_virtual_tab({
                                                  name: l.product_name,
                                              })
                                            : `${l.product_name} — ${l.lot_code}`}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </div>
                        <div className="flex-1 overflow-auto mt-4">
                            {lots.map((l) => (
                                <TabsContent
                                    key={l.inventory_lot_id}
                                    value={l.inventory_lot_id}
                                >
                                    {isVirtualLot(l) ? (
                                        <p
                                            data-testid="lot-trace-virtual"
                                            className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200"
                                        >
                                            {debtClosed
                                                ? m.order_lot_trace_virtual_body_cancelled(
                                                      {
                                                          qty: formatQty(
                                                              l.deducted_qty,
                                                          ),
                                                          unit:
                                                              l.product_unit ??
                                                              '',
                                                      },
                                                  )
                                                : m.order_lot_trace_virtual_body(
                                                      {
                                                          qty: formatQty(
                                                              l.deducted_qty,
                                                          ),
                                                          unit:
                                                              l.product_unit ??
                                                              '',
                                                      },
                                                  )}
                                        </p>
                                    ) : (
                                        <LotLineageTimeline
                                            lotId={l.inventory_lot_id}
                                        />
                                    )}
                                </TabsContent>
                            ))}
                        </div>
                    </Tabs>
                )}
            </DialogContent>
        </Dialog>
    )
}
